import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Eye, EyeOff, Loader2 } from 'lucide-react';
import { apiRequest, formatRelativeTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

export default function Notifications() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showRead, setShowRead] = useState(false);

  const fetchNotifications = async () => {
    try {
      const data = await apiRequest('/notifications');
      // API returns {notifications: [...], unread_count, total}
      setNotifications(data?.notifications || data || []);
    } catch (error) {
      toast.error('Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkRead = async (notificationId) => {
    try {
      await apiRequest(`/notifications/${notificationId}/read`, { method: 'POST' });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      // Dispatch event to update Header badge
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiRequest('/notifications/mark-all-read', { method: 'POST' });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
      // Dispatch event to notify Header to refresh count
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'task_assigned':
        return '📋';
      case 'task_completed':
        return '✅';
      case 'mention':
        return '💬';
      case 'blocker_reported':
        return '🚨';
      case 'group_added':
        return '👥';
      default:
        return '🔔';
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const readCount = notifications.filter((n) => n.is_read).length;
  const visibleNotifications = showRead
    ? notifications
    : notifications.filter((n) => !n.is_read);

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Inter' }}>
            Notifications
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up"}
            {readCount > 0 && (
              <span className="text-slate-400"> · {readCount} read</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {readCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRead((v) => !v)}
              data-testid="toggle-show-read-button"
            >
              {showRead ? (
                <>
                  <EyeOff className="mr-2 h-4 w-4" />
                  Hide Read
                </>
              ) : (
                <>
                  <Eye className="mr-2 h-4 w-4" />
                  Show Read ({readCount})
                </>
              )}
            </Button>
          )}
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead} data-testid="mark-all-read-button">
              <CheckCheck className="mr-2 h-4 w-4" />
              Mark All Read
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#DC2626]" />
        </div>
      ) : visibleNotifications.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {visibleNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 cursor-pointer transition-colors ${
                    notification.is_read ? 'bg-white opacity-70' : 'bg-red-50/50'
                  } hover:bg-slate-50`}
                  onClick={() => handleNotificationClick(notification)}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{getNotificationIcon(notification.type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`font-medium text-sm ${notification.is_read ? 'text-slate-700' : 'text-slate-900'}`}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <div className="w-2 h-2 rounded-full bg-[#DC2626] flex-shrink-0 mt-2" />
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{notification.message}</p>
                      <p className="text-xs text-slate-400 mt-2">{formatRelativeTime(notification.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="empty-state py-16 text-center" data-testid="notifications-empty-state">
          <Bell className="empty-state-icon mx-auto h-16 w-16 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mt-4">
            {readCount > 0 && !showRead ? 'No new notifications' : 'No notifications'}
          </h3>
          <p className="text-slate-500 mt-2">
            {readCount > 0 && !showRead
              ? `You have ${readCount} read notification${readCount === 1 ? '' : 's'} hidden.`
              : "You're all caught up!"}
          </p>
          {readCount > 0 && !showRead && (
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => setShowRead(true)}
              data-testid="empty-show-read-button"
            >
              <Eye className="mr-2 h-4 w-4" />
              Show Read ({readCount})
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
